import { ROLE_CODE_BY_USER_ROLE, USER_ROLE_BY_CODE, type RoleCode } from '@/config/role-codes';
import type { Permission } from '@/config/permissions';
import type { AccessSnapshot, ModulePermissions, UserProfileRole } from '@/types/access';
import type { UserRole } from '@/types/role';

export const EMPTY_ACCESS_SNAPSHOT: AccessSnapshot = {
  roleCodes: new Set(),
  permissionKeys: new Set(),
  modules: new Set(),
};

export const createAccessSnapshot = (input: {
  roles: readonly UserProfileRole[];
  permissions: readonly ModulePermissions[];
  modules: readonly string[];
}): AccessSnapshot => {
  const roleCodes = new Set(input.roles.filter((role) => role.isActive).map((role) => role.code));
  const permissionKeys = new Set<string>();
  input.permissions.forEach((modulePermissions) => {
    modulePermissions.permissions.forEach((permission) => {
      permissionKeys.add(permission.featureArea);
    });
  });
  return {
    roleCodes,
    permissionKeys,
    modules: new Set(input.modules),
  };
};

export const getPrimaryRoleCode = (roles: readonly UserProfileRole[]): RoleCode | null => {
  const activeRole = roles.find((role) => role.isActive);
  if (!activeRole) return null;
  const mapped = USER_ROLE_BY_CODE[activeRole.code as RoleCode];
  return mapped ? (activeRole.code as RoleCode) : null;
};

export const getPrimaryUserRole = (roles: readonly UserProfileRole[]): UserRole => {
  const roleCode = getPrimaryRoleCode(roles);
  if (!roleCode) return 'Employee';
  return USER_ROLE_BY_CODE[roleCode] ?? 'Employee';
};

const isKnownRoleCode = (code: string): code is RoleCode => code in USER_ROLE_BY_CODE;

export const getActiveUserRole = (
  roles: readonly UserProfileRole[],
  preferredRole?: string | null
): UserRole => {
  if (preferredRole && isKnownRoleCode(preferredRole)) {
    return USER_ROLE_BY_CODE[preferredRole];
  }
  return getPrimaryUserRole(roles);
};

export const hasRoleCode = (snapshot: AccessSnapshot, roleCode: RoleCode | string): boolean =>
  snapshot.roleCodes.has(roleCode);

export const hasAnyRoleCode = (
  snapshot: AccessSnapshot,
  roleCodes: readonly (RoleCode | string)[]
): boolean => roleCodes.some((code) => snapshot.roleCodes.has(code));

export const hasUserRole = (snapshot: AccessSnapshot, role: UserRole): boolean =>
  hasRoleCode(snapshot, ROLE_CODE_BY_USER_ROLE[role]);

export const hasPermission = (snapshot: AccessSnapshot, permission: Permission | string): boolean =>
  snapshot.permissionKeys.has(permission);

export const hasAnyPermission = (
  snapshot: AccessSnapshot,
  permissions: readonly (Permission | string)[]
): boolean => permissions.some((permission) => hasPermission(snapshot, permission));

export const hasAllPermissions = (
  snapshot: AccessSnapshot,
  permissions: readonly (Permission | string)[]
): boolean => permissions.every((permission) => hasPermission(snapshot, permission));

export const hasModuleAccess = (snapshot: AccessSnapshot, moduleCode: string): boolean =>
  snapshot.modules.has(moduleCode);
